import { isStatusCritical, isStatusUnauthorized } from '../services/error';
import type { AxiosError } from 'axios';

const USER_BANNED = 'ACCESS_DENIED_TO_BANNED_USER';
const GOOGLE_PAY_DECLINED = 'AUTH_REQUESTED_PAYMENT_CREATION_ERROR';

interface ErrorState {
  status: number | null;
  redirectLink: string | null;
  error: Record<string, unknown> | AxiosError;
}

interface ErrorResponse {
  response?: {
    status?: number;
    headers?: {
      location?: string;
    };
    data?: {
      code?: string | null;
    };
  };
}

export default {
  namespaced: true,
  state: {
    status: null,
    redirectLink: null,
    error: {},
  } as ErrorState,
  getters: {
    status: (state: ErrorState): number | null => state.status,
    error: (state: ErrorState): Record<string, unknown> | AxiosError => state.error,
    isCriticalError: (state: ErrorState): boolean => isStatusCritical(state.status),
    isUnauthorized: (state: ErrorState): boolean => isStatusUnauthorized(state.status ?? 0),
    hasRedirectLink: (state: ErrorState): boolean => state.redirectLink !== null,
    redirectLink: (state: ErrorState): string | null => state.redirectLink,
    errorCode: (state: ErrorState): string | null | undefined => {
      const error = state.error as ErrorResponse;
      const { response = {} } = error || {};
      const { data = { code: null } } = response;

      return data.code;
    },
    isUserBanned: (state: ErrorState, getters: { errorCode: string | null | undefined }): boolean => getters.errorCode === USER_BANNED,
    isGooglePayDeclined: (state: ErrorState, getters: { errorCode: string | null | undefined }): boolean => getters.errorCode === GOOGLE_PAY_DECLINED,
  },
  mutations: {
    setStatus(state: ErrorState, payload: number | null): void {
      state.status = payload;
    },
    setRedirectLink(state: ErrorState, payload: string | null): void {
      state.redirectLink = payload;
    },
    setError(state: ErrorState, payload: Record<string, unknown> | AxiosError): void {
      state.error = payload;
    },
    resetState(state: ErrorState): void {
      state.error = {};
      state.status = null;
      state.redirectLink = null;
    },
  },
  actions: {
    setError({ commit }: { commit: (mutation: string, payload?: unknown) => void }, error: AxiosError | ErrorResponse): void {
      const response = (error as AxiosError).response || error;

      const { status, headers } = response as { status?: number; headers?: { location?: string } };

      commit('setStatus', status ?? null);

      if (headers && 'location' in headers) {
        commit('setRedirectLink', headers.location ?? null);
      }

      commit('setError', error);
    },
    resetState({ commit }: { commit: (mutation: string) => void }): void {
      commit('resetState');
    },
  },
};
